import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DataContext } from '../../Provider/dataProvider';
import { fetchWithAuth, getAuthHeader } from '../../utils/authUtils';
import { AiActionsWidget } from '../../components/AiSuggestions';
import MembershipRequiredView from '../../components/MembershipRequiredView';
import { isMembershipDeniedError } from '../../utils/membershipAccess';
import { FEATURES } from '../../constants/featureAccess';
import { useElderlyFeatureAccess, useElderlyMembershipMap } from '../../hooks/useFeatureAccess';
import '../../css/medicalHistory.css';
import '../../css/aiSuggestions.css';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

function uploadErrorMessage(response, payload, fallback) {
	if (response?.status === 413) {
		return 'Không thể thêm thông tin khám bệnh. Kích thước ảnh quá to';
	}
	return payload?.message || fallback;
}

const formatDate = (value) => {
	if (!value) return 'N/A';
	const date = new Date(value);
	return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleDateString('vi-VN');
};

const MEDICATION_WEEK_DAYS = [1, 2, 3, 4, 5, 6, 0];

function parseMedicationDayOfWeeks(medication) {
	const raw = medication?.dayOfWeeks;
	if (raw === null || raw === undefined || raw === '') return [];
	return [...new Set(
		String(raw).split(',')
			.map((d) => Number(String(d).trim()))
			.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6)
	)].sort((a, b) => a - b);
}

function formatMedicationDaysLabel(selectedDays) {
	if (!selectedDays?.length) return 'Mỗi ngày';
	return `Chọn: ${selectedDays.map((d) => (d === 0 ? 'CN' : `T${d + 1}`)).join(', ')}`;
}

function MedicationDaysField({ selectedDays, onChange }) {
	return (
		<label className="tamcare-form-grid--full">
			Các ngày uống
			<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
				{MEDICATION_WEEK_DAYS.map((day) => {
					const active = (selectedDays || []).includes(day);
					return (
						<button
							key={day}
							type="button"
							onClick={() => {
								const current = selectedDays || [];
								onChange(active ? current.filter((d) => d !== day) : [...current, day]);
							}}
							style={{
								padding: '8px 16px',
								borderRadius: 999,
								border: '1px solid #cbd5e1',
								background: active ? '#0f766e' : 'transparent',
								color: active ? '#ffffff' : '#334155',
								fontWeight: active ? 700 : 400,
								cursor: 'pointer',
								transition: 'all 0.2s',
							}}
						>
							{day === 0 ? 'CN' : `T${day + 1}`}
						</button>
					);
				})}
			</div>
			<div style={{ marginTop: 6, color: '#64748b', fontSize: 14 }}>
				{formatMedicationDaysLabel(selectedDays)}
			</div>
		</label>
	);
}

const MedicalHistory = () => {
	const i18nOrLoading = (isLoading, okText = 'Lưu thông tin khám bệnh', loadingText = 'Đang lưu...') => {
		if (isLoading) return loadingText;
		return okText;
	};

	const { profileId } = useParams();
	const { data } = useContext(DataContext);
	const [medicalRecords, setMedicalRecords] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [savingRecord, setSavingRecord] = useState(false);
	const [savingMedication, setSavingMedication] = useState(false);
	const [recordMessage, setRecordMessage] = useState('');
	const [medicationMessage, setMedicationMessage] = useState('');
	const [assignMessage, setAssignMessage] = useState('');
	const [assigningRecordId, setAssigningRecordId] = useState(null);
	const [assignConfirmOpen, setAssignConfirmOpen] = useState(false);
	const [pendingAssign, setPendingAssign] = useState(null);
	const [profileData, setProfileData] = useState(null);
	const [recordForm, setRecordForm] = useState({
		hospitalName: '',
		diagnosis: '',
		doctorNotes: '',
		visitDate: new Date().toISOString().split('T')[0],
		revisitAt: '',
		revisitRemindBeforeMinutes: 60,
		documentFile: null,
	});
	const [medicationForm, setMedicationForm] = useState({
		medicalRecordId: '',
		medicationName: '',
		dosage: '',
		startDate: '',
		endDate: '',
		note: '',
		schedulesText: '08:00,20:00',
		elderlyId: '',
		selectedDays: [],
	});
	const [newScheduleTime, setNewScheduleTime] = useState('08:00');
	const [viewerOpen, setViewerOpen] = useState(false);
	const [viewerRecord, setViewerRecord] = useState(null);

	const [viewerMedications, setViewerMedications] = useState([]);
	const [viewerLoading, setViewerLoading] = useState(false);
	const [viewerError, setViewerError] = useState('');
	const [previewSrc, setPreviewSrc] = useState('');
	const [editMedicationModalOpen, setEditMedicationModalOpen] = useState(false);
	const [savingEditedMedication, setSavingEditedMedication] = useState(false);
	const [editMedicationMessage, setEditMedicationMessage] = useState('');
	const [editingMedication, setEditingMedication] = useState(null);
	const [newEditScheduleTime, setNewEditScheduleTime] = useState('08:00');
	const [addMedicationModalOpen, setAddMedicationModalOpen] = useState(false);
	const [editRecordModalOpen, setEditRecordModalOpen] = useState(false);
	const [savingEditedRecord, setSavingEditedRecord] = useState(false);
	const [editRecordMessage, setEditRecordMessage] = useState('');
	const [editingRecord, setEditingRecord] = useState(null);
	const [linkedElderlyId, setLinkedElderlyId] = useState('');
	const [linkedElderlies, setLinkedElderlies] = useState([]);
	const [loadingElderlies, setLoadingElderlies] = useState(false);
	const [selectedElderlyForNewRecord, setSelectedElderlyForNewRecord] = useState('');

	const [scanModalOpen, setScanModalOpen] = useState(false);
	const [scanFile, setScanFile] = useState(null);
	const [scanning, setScanning] = useState(false);
	const [scanMessage, setScanMessage] = useState('');
	const [scanDocumentUrl, setScanDocumentUrl] = useState('');
	const [scanFields, setScanFields] = useState({ hospitalName: '', diagnosis: '', doctorNotes: '', visitDate: '', revisitAt: '', revisitRemindBeforeMinutes: 60 });

	// Modal thêm hồ sơ khám mới (Relative thao tác)
	const [addRecordModalOpen, setAddRecordModalOpen] = useState(false);
	const [addMedicationSectionOpen, setAddMedicationSectionOpen] = useState(false);
	const [createdRecordId, setCreatedRecordId] = useState(null);

	const currentRole = String(data?.role || '').toLowerCase();
	const isRelative = currentRole === 'relative';
	const noParamProfileId = !profileId;
	const parseSchedulesText = (value) => String(value || '')
		.split(',')
		.map((item) => item.trim())
		.filter(Boolean);

	const mergeTimesText = (text, time) => {
		const arr = parseSchedulesText(text);
		if (!time) return arr.join(',');
		const norm = String(time).slice(0, 5);
		const next = Array.from(new Set([...arr, norm])).sort();
		return next.join(',');
	};

	const removeTimeFromText = (text, time) => {
		return parseSchedulesText(text).filter((t) => t !== time).join(',');
	};

	const toggleDay = (day) => {
		setMedicationForm((prev) => {
			const current = prev.selectedDays || [];
			if (current.includes(day)) {
				return { ...prev, selectedDays: current.filter((d) => d !== day) };
			} else {
				return { ...prev, selectedDays: [...current, day] };
			}
		});
	};

	const isMedicationActive = (value) => Number(value) === 1;

	const resolvedProfileId = useMemo(() => {
		if (profileId) return profileId;
		if (isRelative) return linkedElderlyId || '';
		return data?.id || '';
	}, [profileId, isRelative, data?.id, linkedElderlyId]);

	const { canUse: canUseForElderly, loading: accessLoading } = useElderlyFeatureAccess(resolvedProfileId);
	const elderlyIds = useMemo(() => linkedElderlies.map((el) => el.id), [linkedElderlies]);
	const { canUseFor: canManageElderly } = useElderlyMembershipMap(elderlyIds, FEATURES.MEDICAL_RECORDS);
	const manageableElderlies = useMemo(
		() => linkedElderlies.filter((el) => canManageElderly(String(el.id))),
		[linkedElderlies, canManageElderly]
	);
	const membershipLocked = Boolean(
		resolvedProfileId
		&& data?.role !== 'admin'
		&& !accessLoading
		&& !canUseForElderly(FEATURES.MEDICAL_RECORDS)
	);
	const hasManageableElderly = manageableElderlies.length > 0;
	const allElderliesHaveMembership = linkedElderlies.length > 0
		&& linkedElderlies.every((el) => canManageElderly(String(el.id)));
	const showElderlySelector = isRelative && noParamProfileId && !allElderliesHaveMembership;
	const showCompactElderlySelect = isRelative && noParamProfileId && allElderliesHaveMembership && linkedElderlies.length > 1;

	const heroProfileId = useMemo(() => {
		return profileId || resolvedProfileId || '';
	}, [profileId, resolvedProfileId]);

	useEffect(() => {
		if (!isRelative) return;

		const fetchLinkedElderlies = async () => {
			try {
				setLoadingElderlies(true);
				const response = await fetchWithAuth(`${API_BASE}/relatives`);

				const payload = await response.json().catch(() => ({}));
				if (!response.ok || !payload?.success) {
					setLinkedElderlies([]);
					return;
				}

				const elderlies = Array.isArray(payload?.data) ? payload.data : [];
				setLinkedElderlies(elderlies);
				if (!profileId && elderlies.length > 0) {
					setLinkedElderlyId(String(elderlies[0].id));
				}
				setSelectedElderlyForNewRecord('');
			} catch (err) {
				setLinkedElderlies([]);
			} finally {
				setLoadingElderlies(false);
			}
		};

		fetchLinkedElderlies();
	}, [isRelative, profileId]);

	useEffect(() => {
		if (profileId) {
			setLinkedElderlyId(String(profileId));
		}
	}, [profileId]);

	const fetchMedicalHistory = async (targetId) => {
		if (!targetId) {
			return null;
		}

		const endpoints = [
			`${API_BASE}/medical-records/${targetId}`,
			`${API_BASE}/api/medical-records/elderly/${targetId}`,
		];

		for (const endpoint of endpoints) {
			const response = await fetchWithAuth(endpoint);

			if (response.status === 403) {
				const denied = await response.json().catch(() => ({}));
				const err = new Error(
					denied?.message || 'Không có quyền xem hồ sơ này. Kiểm tra gói hội viên cho đúng người cao tuổi.'
				);
				err.code = denied?.code;
				err.isMembershipDenied = true;
				throw err;
			}

			if (!response.ok) {
				continue;
			}

			const payload = await response.json();
			const historyData = payload?.data || payload;
			return Array.isArray(historyData?.medicalRecords)
				? historyData.medicalRecords
				: Array.isArray(historyData)
					? historyData
					: [];
		}

		return null;
	};

	useEffect(() => {
		if (!resolvedProfileId || membershipLocked) {
			if (membershipLocked) {
				setMedicalRecords([]);
				setError(null);
				setLoading(false);
			}
			return;
		}

		const fetchMedicalRecords = async () => {
			try {
				setLoading(true);
				setError(null);

				const records = await fetchMedicalHistory(resolvedProfileId);

				if (records === null) {
					throw new Error('Không thể tải lịch sử bệnh lý');
				}

				setMedicalRecords(records);
			} catch (err) {
				setMedicalRecords([]);
				setError(err.message || 'Không thể tải lịch sử bệnh lý');
			} finally {
				setLoading(false);
			}
		};

		fetchMedicalRecords();
	}, [resolvedProfileId, membershipLocked]);

	useEffect(() => {
		if (!heroProfileId) return;

		const fetchProfile = async () => {
			try {
				const response = await fetchWithAuth(`${API_BASE}/accounts/${heroProfileId}`);
				const payload = await response.json().catch(() => ({}));
				if (!response.ok || !payload?.success) return;
				setProfileData(payload.data || null);
			} catch (err) {
				setProfileData(null);
			}
		};

		fetchProfile();
	}, [heroProfileId]);

	const fetchViewerMedications = async (recordId) => {
		if (!recordId) {
			setViewerMedications([]);
			return;
		}
		setViewerLoading(true);
		setViewerError('');
		setViewerMedications([]);
		try {
			const response = await fetchWithAuth(`${API_BASE}/api/medications/record/${recordId}`);
			if (!response.ok) {
				const errorBody = await response.json().catch(() => ({}));
				throw new Error(errorBody.message || 'Không thể tải danh sách thuốc');
			}
			const payload = await response.json();
			setViewerMedications(Array.isArray(payload?.data) ? payload.data : []);
		} catch (err) {
			setViewerError(err.message || 'Không thể tải danh sách thuốc');
		} finally {
			setViewerLoading(false);
		}
	};

	const openDocument = async (record) => {
		if (!record?.documentUrl) return;
		const fullUrl = `${API_BASE}${record.documentUrl}`;
		if (record.documentUrl.toLowerCase().endsWith('.pdf')) {
			window.open(fullUrl, '_blank', 'noopener,noreferrer');
			return;
		}

		setViewerRecord(record);
		setPreviewSrc(fullUrl);
		setViewerMedications([]);
		setViewerError('');
		setViewerOpen(true);
		await fetchViewerMedications(record.id);
	};

	const openMedications = async (record) => {
		if (!record?.id) return;
		setViewerRecord(record);
		setViewerOpen(true);
		setPreviewSrc(record?.documentUrl ? `${API_BASE}${record.documentUrl}` : '');
		await fetchViewerMedications(record.id);
	};

	const refreshMedicalRecords = async () => {
		if (!resolvedProfileId) return;
		const records = await fetchMedicalHistory(resolvedProfileId);
		if (records === null) return;
		setMedicalRecords(records);
	};

	const handleAssignElderly = async (recordId, elderlyId) => {
		if (!recordId || !elderlyId) return;
		if (!canManageElderly(String(elderlyId))) {
			setAssignMessage('Người cao tuổi này chưa có gói hội viên. Vui lòng mua gói trước khi gán hồ sơ.');
			return;
		}
		setPendingAssign({ recordId, elderlyId });
		setAssignConfirmOpen(true);
	};

	const confirmAssignElderly = async () => {
		if (!pendingAssign?.recordId || !pendingAssign?.elderlyId) {
			setAssignConfirmOpen(false);
			setPendingAssign(null);
			return;
		}
		try {
			setAssigningRecordId(pendingAssign.recordId);
			setAssignMessage('');
			const response = await fetchWithAuth(`${API_BASE}/medical-records/${pendingAssign.recordId}/assign`, {
				method: 'PATCH',
				headers: {
					
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({ elderlyId: pendingAssign.elderlyId }),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể cập nhật người cao tuổi');
			}

			setAssignMessage('Đã cập nhật người cao tuổi cho đợt khám.');
			await refreshMedicalRecords();
		} catch (err) {
			setAssignMessage(err.message || 'Không thể cập nhật người cao tuổi');
		} finally {
			setAssigningRecordId(null);
			setAssignConfirmOpen(false);
			setPendingAssign(null);
		}
	};

	const handleAddRecord = async (event) => {
		event.preventDefault();
		try {
			setSavingRecord(true);
			setRecordMessage('');

			// Validate elderly selection
			const targetElderly = String(selectedElderlyForNewRecord || profileId || '').trim();
			if (!targetElderly) {
				setRecordMessage('⚠️ Vui lòng chọn người cao tuổi để ghi nhận khám bệnh.');
				setSavingRecord(false);
				return;
			}
			if (!canManageElderly(targetElderly)) {
				setRecordMessage('Người cao tuổi này chưa có gói hội viên. Vui lòng mua gói để tạo hồ sơ.');
				setSavingRecord(false);
				return;
			}
			const formData = new FormData();
			formData.append('hospitalName', recordForm.hospitalName);
			formData.append('diagnosis', recordForm.diagnosis);
			formData.append('doctorNotes', recordForm.doctorNotes);
			formData.append('visitDate', recordForm.visitDate);
			formData.append('revisitAt', recordForm.revisitAt || '');
			formData.append('revisitRemindBeforeMinutes', String(recordForm.revisitRemindBeforeMinutes || 60));
			if (recordForm.documentFile) {
				formData.append('document', recordForm.documentFile);
			}

			const response = await fetchWithAuth(`${API_BASE}/api/medical-records/${targetElderly}`, {
				method: 'POST',
				headers: {  },
				body: formData,
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
throw new Error(uploadErrorMessage(response, payload, 'Không thể thêm thông tin khám bệnh'));
			}

			setRecordMessage('Đã thêm thông tin khám bệnh thành công.');
			setRecordForm({
				hospitalName: '',
				diagnosis: '',
				doctorNotes: '',
				visitDate: new Date().toISOString().split('T')[0],
				revisitAt: '',
				revisitRemindBeforeMinutes: 60,
				documentFile: null,
			});
			// Tạo thành công -> refresh timeline
			const createdId = payload?.data?.id || payload?.id || null;
			await refreshMedicalRecords();

			// Nếu user muốn nhập thuốc ngay, giữ modal và mở section nhập thuốc
			if (addMedicationSectionOpen) {
				// Ưu tiên gắn thuốc vào record vừa tạo

				// Tìm bản ghi vừa tạo (ưu tiên lấy id nếu backend trả về; nếu không thì dựa vào timestamp gần nhất)
				let nextId = createdId || createdRecordId;

				if (!nextId) {
						const sorted = Array.isArray(medicalRecords)
							? medicalRecords.slice().sort((a, b) => new Date(b.visitDate || 0) - new Date(a.visitDate || 0))
							: [];
						// fallback: lấy record có visitDate trùng giá trị vừa nhập
						const match = sorted.find((r) => String(r.visitDate || '').slice(0, 10) === String(recordForm.visitDate || '').slice(0, 10));
						nextId = match?.id;
					}

				setCreatedRecordId(nextId || null);
				setMedicationForm((prev) => ({
					...prev,
					medicalRecordId: nextId ? String(nextId) : '',
				}));
				setAddMedicationSectionOpen(true);
				return;
			}

			// Mặc định: đóng modal
			setAddRecordModalOpen(false);
			setAddMedicationSectionOpen(false);
		} catch (err) {
			setRecordMessage(err.message);
		} finally {
			setSavingRecord(false);
		}
	};

		const handleAddMedication = async (event) => {
			event.preventDefault();
			if (!medicationForm.medicalRecordId) {
				setMedicationMessage('Vui lòng chọn đợt khám để gắn đơn thuốc.');
				return;
			}

		// 🔴 Validate required dates
		if (!medicationForm.startDate || !medicationForm.endDate) {
			setMedicationMessage('⚠️ Vui lòng nhập ngày bắt đầu và ngày kết thúc. Hệ thống cần thông tin này để tạo lịch nhắc uống thuốc chính xác.');
			return;
		}

		// Validate end_date >= start_date
		if (new Date(medicationForm.endDate) < new Date(medicationForm.startDate)) {
			setMedicationMessage('⚠️ Ngày kết thúc phải sau hoặc bằng ngày bắt đầu.');
			return;
		}

		try {
			setSavingMedication(true);
			setMedicationMessage('');
			const times = medicationForm.schedulesText.split(',').map((item) => item.trim()).filter(Boolean);
const selectedDays = medicationForm.selectedDays || [];
const schedules = times.map((time) => ({
	time,
	dayOfWeek: selectedDays.length > 0 ? selectedDays : null
}));

			const response = await fetchWithAuth(`${API_BASE}/api/medications/add`, {
				method: 'POST',
				headers: {
					
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
						elderlyId: medicationForm.elderlyId || selectedElderlyForNewRecord || resolvedProfileId,
					medicalRecordId: medicationForm.medicalRecordId || null,
					medicationName: medicationForm.medicationName,
					dosage: medicationForm.dosage,
					startDate: medicationForm.startDate || null,
					endDate: medicationForm.endDate || null,
					note: medicationForm.note,
					schedules,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể thêm đơn thuốc');
			}

			setMedicationMessage('Đã thêm đơn thuốc thành công.');
			setMedicationForm({
				medicalRecordId: '',
				medicationName: '',
				dosage: '',
				startDate: '',
				endDate: '',
				note: '',
				schedulesText: '08:00,20:00',
				elderlyId: '',
				selectedDays: [],
			});
			await refreshMedicalRecords();
		} catch (err) {
			setMedicationMessage(err.message);
		} finally {
			setSavingMedication(false);
		}
	};

	const handleScanUpload = async (event) => {
		event.preventDefault();
		if (!scanFile) {
			setScanMessage('Vui lòng chọn ảnh/PDF để quét.');
			return;
		}
		try {
			setScanning(true);
			setScanMessage('');
			const t0 = Date.now();

			const formData = new FormData();
			formData.append('document', scanFile);
			const response = await fetchWithAuth(`${API_BASE}/api/medical-records/scan?nocache=1`, {
				method: 'POST',
				headers: {  },
				body: formData,
			});

			const payload = await response.json().catch(() => ({}));

			if (!response.ok || !payload?.success) {
				throw new Error(payload?.message || 'Không thể quét ảnh hồ sơ');
			}
			const docUrl = payload?.data?.documentUrl || '';
			const parsed = payload?.data?.parsed || {};
			const ocrText = String(payload?.data?.text || '');
			const rawDate = parsed?.visitDate || '';
			const toInputDate = (v) => {
				if (!v) return '';
				const m = String(v).match(/^(\d{2})[\/-](\d{2})[\/-](\d{4})$/);
				if (m) return `${m[3]}-${m[2]}-${m[1]}`;
				const d = new Date(v);
				return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
			};
			const lines = ocrText.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
			// Prefer extracting from JSON-like content if present to avoid capturing whole line
			const jsonPick = (key) => {
				try {
					const re = new RegExp('"' + key + '"\\s*:\\s*"((?:\\\\.|[^"\\\\])*)"', 'i');
					const m = ocrText.match(re);
					return m ? m[1].trim() : '';
				} catch { return ''; }
			};
			const stripJsonDecorations = (v) => {
				let s = String(v || '').trim();
				// remove trailing comma first
				if (s.endsWith(',')) s = s.slice(0, -1).trim();
				// remove wrapping quotes
				if ((s.startsWith('"') && s.endsWith('"')) || (s.startsWith("'") && s.endsWith("'"))) {
					s = s.slice(1, -1).trim();
				}
				return s.trim();
			};
			const extractFieldSmart = (pattern) => {
				try {
					const re = new RegExp(pattern, 'i');
					const text = ocrText;
					const m = re.exec(text);
					if (!m) return '';
					const startIdx = m.index;
					// Try quoted segment around the match
					const leftQ = text.lastIndexOf('"', startIdx);
					const rightQ = text.indexOf('"', startIdx + m[0].length);
					if (leftQ !== -1 && rightQ !== -1 && rightQ > leftQ) {
						const seg = text.slice(leftQ + 1, rightQ).trim();
						return stripJsonDecorations(seg);
					}
					// Fallback: value after nearest colon, until next comma/brace
					let after = text.indexOf(':', startIdx);
					if (after === -1) return '';
					let raw = text.slice(after + 1).trim();
					const commaIdx = raw.indexOf(',');
					const braceIdx = raw.indexOf('}');
					let end = raw.length;
					if (commaIdx !== -1) end = Math.min(end, commaIdx);
					if (braceIdx !== -1) end = Math.min(end, braceIdx);
					raw = raw.slice(0, end).trim();
					return stripJsonDecorations(raw);
				} catch { return ''; }
			};
			const sanitizeHospital = (v) => {
				let s = String(v || '').trim();
				// Trim any trailing JSON tail like ","visitDate": or ","diagnosis": or ","doctorNotes":
				const tail = s.search(/",\s*"(visitDate|diagnosis|doctorNotes)"\s*:/);
				if (tail > -1) s = s.slice(0, tail);
				return stripJsonDecorations(s);
			};
			const extractField = (pattern) => {
				try {
					const re = new RegExp(pattern, 'i');
					const line = lines.find(l => re.test(l));
					if (!line) return '';
					const idx = line.indexOf(':');
					const raw = idx >= 0 ? line.slice(idx + 1).trim() : line.trim();
					return stripJsonDecorations(raw);
				} catch {
					return '';
				}
			};
			const findDate = () => {
				const m = ocrText.match(/(\b\d{2}[\/-]\d{2}[\/-]\d{4}\b|\b\d{4}-\d{2}-\d{2}\b)/);
				return m ? m[1] : '';
			};
			const fbHospital = jsonPick('hospitalName') || extractFieldSmart('(bệnh viện|benh vien|phòng khám|phong kham|clinic|hospital|\\bbv\\b|\\bpk\\b)');
			const fbDiagnosis = jsonPick('diagnosis') || extractFieldSmart('(chẩn đoán|chan doan|diagnosis)');
			const fbNotes = jsonPick('doctorNotes') || extractFieldSmart('(lời dặn|loi dan|dặn dò|dan do|ghi chú|ghi chu|note|notes)');
			const fbVisitDate = jsonPick('visitDate') || findDate();

			setScanDocumentUrl(docUrl);
			const merged = {
				...scanFields,
				hospitalName: parsed?.hospitalName || sanitizeHospital(fbHospital) || scanFields.hospitalName || '',
				diagnosis: parsed?.diagnosis || fbDiagnosis || scanFields.diagnosis || '',
				doctorNotes: parsed?.doctorNotes || fbNotes || scanFields.doctorNotes || '',
				visitDate: toInputDate(rawDate || fbVisitDate) || scanFields.visitDate || new Date().toISOString().slice(0, 10),
			};

			setScanFields(merged);
			setScanMessage(payload?.ocrConfigured ? 'Đã quét xong, vui lòng kiểm tra và bấm Lưu thông tin khám bệnh.' : 'OCR chưa cấu hình. Bạn có thể nhập tay rồi bấm Lưu thông tin khám bệnh.');

		} catch (err) {
			setScanMessage(err.message || 'Lỗi quét ảnh hồ sơ');
		} finally {
			setScanning(false);
		}
	};

	const handleCreateRecordFromScan = async () => {
		const targetElderly = String((profileId ? profileId : selectedElderlyForNewRecord) || '').trim();
		if (!targetElderly) {
			setScanMessage('⚠️ Vui lòng chọn người cao tuổi để ghi nhận.');
			return;
		}
		if (!canManageElderly(targetElderly)) {
			setScanMessage('Người cao tuổi này chưa có gói hội viên. Vui lòng mua gói để tạo hồ sơ.');
			return;
		}
		if (!scanFields.diagnosis) {
			setScanMessage('⚠️ Vui lòng nhập Chẩn đoán.');
			return;
		}
		try {
			setScanning(true);
			setScanMessage('');
			const body = {
				hospitalName: scanFields.hospitalName || '',
				diagnosis: scanFields.diagnosis || '',
				doctorNotes: scanFields.doctorNotes || '',
				visitDate: scanFields.visitDate || new Date().toISOString().split('T')[0],
				revisitAt: scanFields.revisitAt || null,
				revisitRemindBeforeMinutes: scanFields.revisitRemindBeforeMinutes || 60,
				documentUrl: scanDocumentUrl || null,
			};
			const resp = await fetchWithAuth(`${API_BASE}/medical-records/${targetElderly}`, {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(body),
			});
			const payload = await resp.json().catch(() => ({}));
			if (!resp.ok) {
				throw new Error(payload?.message || 'Không thể tạo hồ sơ');
			}
			setScanMessage('Đã lưu thông tin khám bệnh thành công.');
			setScanModalOpen(false);
			await refreshMedicalRecords();
		} catch (err) {
			setScanMessage(err.message || 'Lỗi tạo hồ sơ');
		} finally {
			setScanning(false);
		}
	};

	const openMedicationEditor = (record, medication) => {
		const dayOfWeeks = parseMedicationDayOfWeeks(medication);
		const uniqTimes = Array.from(new Set(String(medication.remindTimes || medication.remind_times || '').split(',').map((t) => t.trim()).filter(Boolean))).join(',');
		setEditingMedication({
			id: medication.id,
			medicalRecordId: String(medication.medicalRecordId || record?.id || ''),
			medicationName: medication.medicationName || '',
			dosage: medication.dosage || '',
			startDate: medication.startDate ? String(medication.startDate).slice(0, 10) : '',
			endDate: medication.endDate ? String(medication.endDate).slice(0, 10) : '',
			note: medication.note || '',
			schedulesText: uniqTimes,
			isActive: String(Number(medication.isActive) === 0 ? 0 : 1),
			selectedDays: dayOfWeeks,
		});
		setEditMedicationMessage('');
		if (viewerOpen) return;
		setEditMedicationModalOpen(true);
	};

	const openAddMedicationModal = (record) => {
		setMedicationMessage('');
		setMedicationForm((prev) => ({
			...prev,
			medicalRecordId: record?.id ? String(record.id) : prev.medicalRecordId,
			elderlyId: record?.elderlyId ? String(record.elderlyId) : (resolvedProfileId || selectedElderlyForNewRecord || prev.elderlyId),
		}));
		setAddMedicationModalOpen(true);
	};

		const handleEditMedication = async (event) => {
			event.preventDefault();

			if (!editingMedication?.id) return;
			if (!editingMedication.medicalRecordId) {
				setEditMedicationMessage('Vui lòng chọn đợt khám để cập nhật đơn thuốc.');
				return;
			}

		// 🔴 Validate required dates
		if (!editingMedication.startDate || !editingMedication.endDate) {
			setEditMedicationMessage('⚠️ Vui lòng nhập ngày bắt đầu và ngày kết thúc. Hệ thống cần thông tin này để tạo lịch nhắc uống thuốc chính xác.');
			return;
		}

		// Validate end_date >= start_date
		if (new Date(editingMedication.endDate) < new Date(editingMedication.startDate)) {
			setEditMedicationMessage('⚠️ Ngày kết thúc phải sau hoặc bằng ngày bắt đầu.');
			return;
		}

		try {
			setSavingEditedMedication(true);
			setEditMedicationMessage('');
			const times = parseSchedulesText(editingMedication.schedulesText);
			const selectedDays = editingMedication.selectedDays || [];
			const schedules = times.map((time) => ({
				time,
				dayOfWeek: selectedDays.length > 0 ? selectedDays : null
			}));

			const response = await fetchWithAuth(`${API_BASE}/api/medications/${editingMedication.id}`, {
				method: 'PATCH',
				headers: {

					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					medicalRecordId: editingMedication.medicalRecordId || null,
					medicationName: editingMedication.medicationName,
					dosage: editingMedication.dosage,
					startDate: editingMedication.startDate || null,
					endDate: editingMedication.endDate || null,
					note: editingMedication.note,
					isActive: editingMedication.isActive,
					schedules,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể cập nhật đơn thuốc');
			}

			setMedicationMessage('Đã cập nhật đơn thuốc thành công.');
			setEditMedicationMessage('Đã cập nhật đơn thuốc thành công.');
			setEditMedicationModalOpen(false);
			setEditingMedication(null);
			await refreshMedicalRecords();
			if (viewerOpen && viewerRecord?.id) {
				await openMedications(viewerRecord);
			}
		} catch (err) {
			setEditMedicationMessage(err.message);
		} finally {
			setSavingEditedMedication(false);
		}
	};

	const handleDeleteMedication = async (record, medication) => {
		if (!medication?.id) return;
		const confirmed = window.confirm('Bạn có chắc muốn xóa đơn thuốc này không?');
		if (!confirmed) return;

		try {
			const response = await fetchWithAuth(`${API_BASE}/api/medications/${medication.id}`, {
				method: 'DELETE',
				headers: {  },
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể xóa đơn thuốc');
			}

			setMedicationMessage('Đã xóa đơn thuốc thành công.');
			await refreshMedicalRecords();
			if (viewerOpen && viewerRecord?.id === record?.id) {
				await openMedications(viewerRecord);
			}
		} catch (err) {
			setMedicationMessage(err.message);
		}
	};

	const closeAddMedicationModal = () => {
		setAddMedicationModalOpen(false);
		setMedicationMessage('');
	};

	const closeInlineMedicationEditor = () => {
		setEditingMedication(null);
		setEditMedicationMessage('');
	};

	const openRecordEditor = (record) => {
		setEditingRecord({
			id: record.id,
			hospitalName: record.hospitalName || '',
			diagnosis: record.diagnosis || '',
			doctorNotes: record.doctorNotes || '',
			visitDate: record.visitDate ? String(record.visitDate).slice(0, 10) : '',
			revisitAt: record.revisitAt ? new Date(record.revisitAt).toISOString().slice(0, 16) : '',
			revisitRemindBeforeMinutes: Number.isFinite(Number(record.revisitRemindBeforeMinutes)) ? Number(record.revisitRemindBeforeMinutes) : 60,
			documentUrl: record.documentUrl || '',
		});
		setEditRecordMessage('');
		setEditRecordModalOpen(true);
	};

	const handleEditRecord = async (event) => {
		event.preventDefault();
		if (!editingRecord?.id) return;

		try {
			setSavingEditedRecord(true);
			setEditRecordMessage('');
			const response = await fetchWithAuth(`${API_BASE}/medical-records/${editingRecord.id}`, {
				method: 'PATCH',
				headers: {
					
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					hospitalName: editingRecord.hospitalName,
					diagnosis: editingRecord.diagnosis,
					doctorNotes: editingRecord.doctorNotes,
					visitDate: editingRecord.visitDate,
					revisitAt: editingRecord.revisitAt || null,
					revisitRemindBeforeMinutes: Number(editingRecord.revisitRemindBeforeMinutes) || 60,
					documentUrl: editingRecord.documentUrl || null,
				}),
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể cập nhật lịch sử khám');
			}

			setEditRecordMessage('Đã cập nhật lịch sử khám thành công.');
			setEditRecordModalOpen(false);
			setEditingRecord(null);
			await refreshMedicalRecords();
		} catch (err) {
			setEditRecordMessage(err.message);
		} finally {
			setSavingEditedRecord(false);
		}
	};

	const handleDeleteRecord = async (record) => {
		if (!record?.id) return;
		const confirmed = window.confirm('Bạn có chắc muốn xóa lịch sử khám này không?');
		if (!confirmed) return;

		try {
			const response = await fetchWithAuth(`${API_BASE}/medical-records/${record.id}`, {
				method: 'DELETE',
				headers: {  },
			});

			const payload = await response.json().catch(() => ({}));
			if (!response.ok) {
				throw new Error(payload?.message || 'Không thể xóa lịch sử khám');
			}

			await refreshMedicalRecords();
		} catch (err) {
			setMedicationMessage(err.message);
		}
	};

const patientName = profileData?.full_name || profileData?.fullName || medicalRecords[0]?.fullName || data?.username || 'Bệnh nhân TamCare';

// Debug: xem dữ liệu đang dùng để render tamcare-medical-hero
useEffect(() => {

	}, [data, medicalRecords, profileData]);
	const patientConditions = medicalRecords[0]?.diagnosis || profileData?.medical_history_summary || data?.medical_history_summary || 'Chưa có dữ liệu';
	const patientAddress = medicalRecords?.[0]?.address || profileData?.address || data?.address || 'Hà Nội, Việt Nam';
	const patientHeight = profileData?.height || medicalRecords[0]?.height || null;
	const formatMaybeNumber = (v, suffix = '') => {
		if (v === null || v === undefined || v === '') return 'N/A';
		return `${v}${suffix}`;
	};
	const patientGender = profileData?.gender || medicalRecords[0]?.gender || null;
	const patientDob = profileData?.dob || medicalRecords[0]?.dob || null;

	const showMedicalContent = Boolean(
		resolvedProfileId
		&& !accessLoading
		&& !membershipLocked
		&& !loading
		&& (!error || medicalRecords.length > 0)
	);

return (
<div className="tamcare-medical-page">
<div className="tamcare-medical-container tamcare-no-print">
<div className="tamcare-medical-header">
<div>
<h2><i className="fa-solid fa-file-medical"></i> Hồ sơ bệnh án trọn đời</h2>
<p className="tamcare-medical-intro">
	{isRelative
		? allElderliesHaveMembership
			? 'Theo dõi lịch sử khám và đơn thuốc của người cao tuổi đã liên kết.'
			: 'Chọn người cao tuổi để xem hồ sơ. Chỉ người đã mua gói mới được tạo hoặc gán hồ sơ khám.'
		: 'Lịch sử thời gian khám bệnh, xem file gốc và mở danh sách thuốc theo từng đợt khám.'}
</p>
{showCompactElderlySelect && (
	<label className="tamcare-medical-elderly-selector__field tamcare-medical-elderly-selector__field--compact">
		Đang xem hồ sơ của
		<select
			value={linkedElderlyId}
			onChange={(e) => setLinkedElderlyId(e.target.value)}
			className="tamcare-medical-elderly-selector__select"
		>
			{linkedElderlies.map((elderly) => (
				<option key={elderly.id} value={String(elderly.id)}>
					{elderly.fullName || elderly.username || `Cụ ${elderly.id}`}
				</option>
			))}
		</select>
	</label>
)}
</div>
<div className="tamcare-medical-header__actions">
{isRelative && hasManageableElderly && !membershipLocked && (
<>
<button
 type="button"
 className="tamcare-btn tamcare-btn-primary"
 onClick={() => {
 	setAddRecordModalOpen(true);
 	setAddMedicationSectionOpen(false);
 	setCreatedRecordId(null);
 	setSelectedElderlyForNewRecord('');
 	setMedicationForm((prev) => ({ ...prev, elderlyId: '' }));
 }}
>
<i className="fa-solid fa-plus"></i> Thêm Lịch Sử Khám Mới
</button>

<button
 type="button"
 className="tamcare-btn tamcare-btn-secondary"
 onClick={() => {
 	setScanModalOpen(true);
 	setScanMessage('');
 	setScanFile(null);
 	setScanDocumentUrl('');
 	setScanFields({ hospitalName: '', diagnosis: '', doctorNotes: '', visitDate: new Date().toISOString().split('T')[0] });
}}
>
<i className="fa-solid fa-camera"></i> Quét ảnh tạo hồ sơ
</button>
</>
)}
</div>
</div>

{showElderlySelector && (
	<section className="tamcare-medical-elderly-selector">
		<div className="tamcare-medical-elderly-selector__head">
			<p className="tamcare-medical-elderly-selector__eyebrow">Chọn người cao tuổi</p>
			<h3>Vui lòng chọn cụ để xem hồ sơ bệnh án</h3>
		</div>
		{loadingElderlies ? (
			<p className="tamcare-medical-state">Đang tải danh sách người cao tuổi...</p>
		) : linkedElderlies.length === 0 ? (
			<p className="tamcare-medical-state">Chưa có người cao tuổi được liên kết.</p>
		) : (
			<label className="tamcare-medical-elderly-selector__field">
				Người cao tuổi phụ thuộc
				<select
					value={linkedElderlyId}
					onChange={(e) => setLinkedElderlyId(e.target.value)}
					className="tamcare-medical-elderly-selector__select"
				>
					{linkedElderlies.map((elderly) => (
						<option key={elderly.id} value={String(elderly.id)}>
							{elderly.fullName || elderly.username || `Cụ ${elderly.id}`}
							{!canManageElderly(String(elderly.id)) ? ' (Chưa mua gói)' : ''}
						</option>
					))}
				</select>
			</label>
		)}
	</section>
)}

{resolvedProfileId && accessLoading && (
	<p className="tamcare-medical-state">Đang kiểm tra quyền truy cập...</p>
)}

{resolvedProfileId && !accessLoading && membershipLocked && (
	<MembershipRequiredView compact />
)}

{resolvedProfileId && !accessLoading && !membershipLocked && loading && (
	<p className="tamcare-medical-state">Đang tải dữ liệu...</p>
)}

{resolvedProfileId && !accessLoading && !membershipLocked && !loading && error && medicalRecords.length === 0 && (
	isMembershipDeniedError({ message: error })
		? <MembershipRequiredView compact />
		: <p className="tamcare-medical-state tamcare-medical-state--error">Lỗi: {error}</p>
)}

{showMedicalContent && (
<>
<div className="tamcare-medical-hero">
<div className="tamcare-medical-hero__left">
<span className="tamcare-medical-hero__label">Hồ sơ bệnh nhân</span>
<h3>{patientName}</h3>
<p>{patientConditions}</p>
</div>
<div className="tamcare-medical-hero__meta">
<div><strong>Ngày sinh:</strong> {patientDob ? formatDate(patientDob) : 'N/A'}</div>
					<div><strong>Giới tính:</strong> {(() => {
							const raw = patientGender;
							const g = raw ? String(raw).toLowerCase().trim() : '';
							if (!g) return 'N/A';
							// Chuẩn hóa Nam/Nữ từ nhiều format có thể có
							if (g === 'male' || g === 'm' || g === 'nam' || g === '♂' ) return 'Nam';
							if (g === 'female' || g === 'f' || g === 'nữ' || g === 'nu' || g === 'nữ' || g === 'νữ' || g === 'nũ' || g === 'nũ' || g === '♀') return 'Nữ';
							// fallback giữ nguyên giá trị từ DB
							return raw;
						})()}</div>
<div><strong>Chiều cao:</strong> {formatMaybeNumber(patientHeight, ' cm')}</div>
<div><strong>Địa chỉ:</strong> {patientAddress}</div>
</div>
</div>

<AiActionsWidget elderlyId={resolvedProfileId} showTitle={true} />

<section className="tamcare-medical-section">
<div className="tamcare-medical-section-head">
<h3>Lịch sử thời gian khám bệnh</h3>
<span className="tamcare-medical-count">{medicalRecords.length} lần khám</span>
</div>

{medicalRecords.length > 0 ? (
<div className="tamcare-timeline">
{medicalRecords.map((record) => (
<article className="tamcare-timeline-item" key={record.id}>
<div className="tamcare-timeline-dot"></div>
<div className="tamcare-timeline-card">
<div className="tamcare-timeline-head">
<div>
<div className="tamcare-timeline-date">{formatDate(record.visitDate)}</div>
<h4>{record.hospitalName || 'N/A'}</h4>
</div>
			{isRelative && linkedElderlies.length > 0 && (
				<div className="tamcare-elderly-selector">
					<select
						value={record.elderlyId ? String(record.elderlyId) : ''}
						onChange={(e) => handleAssignElderly(record.id, e.target.value)}
						disabled={assigningRecordId === record.id}
					>
						<option value="" disabled>Chọn người cao tuổi</option>
						{linkedElderlies.map((el) => {
							const allowed = canManageElderly(String(el.id));
							return (
								<option key={el.id} value={el.id} disabled={!allowed}>
									{el.fullName || el.username || `#${el.id}`}{!allowed ? ' (Chưa mua gói)' : ''}
								</option>
							);
						})}
					</select>
				</div>
			)}
			<div className="tamcare-timeline-badge">#{record.id}</div>
</div>

<div className="tamcare-timeline-body">
<div><span className="tamcare-info-label">Chẩn đoán:</span> {record.diagnosis}</div>
<div><span className="tamcare-info-label">Lời dặn bác sĩ:</span> {record.doctorNotes || 'N/A'}</div>
</div>

<div className="tamcare-timeline-footer">
{record.documentUrl ? (
<button type="button" className="tamcare-btn tamcare-btn-view" onClick={() => openDocument(record)}>
<i className="fa-solid fa-file-image"></i> Xem file ảnh/PDF
</button>
) : (
<span className="tamcare-muted">Chưa có file đính kèm</span>
)}
<button type="button" className="tamcare-btn tamcare-btn-view tamcare-btn-view--secondary" onClick={() => openMedications(record)}>
<i className="fa-solid fa-pills"></i> Xem Đơn Thuốc
</button>
{isRelative && (
	<button type="button" className="tamcare-btn tamcare-btn-save" onClick={() => openAddMedicationModal(record)}>
		<i className="fa-solid fa-plus"></i> Thêm Đơn Thuốc
	</button>
)}
{isRelative && (
	<button type="button" className="tamcare-btn tamcare-btn-view tamcare-btn--small" onClick={() => openRecordEditor(record)}>
		Sửa lịch sử khám
	</button>
)}
{isRelative && (
	<button type="button" className="tamcare-btn tamcare-btn--danger tamcare-btn--small" onClick={() => handleDeleteRecord(record)}>
		Xóa lịch sử khám
	</button>
)}
</div>

{Array.isArray(record.medications) && record.medications.length > 0 && (
<div className="tamcare-medication-inline">
<h5>Thuốc theo đợt khám</h5>
<ul>
{record.medications.map((med) => (
<li key={med.id}>
<div className={`tamcare-medication-chip ${isMedicationActive(med.isActive) ? 'tamcare-medication-chip--active' : 'tamcare-medication-chip--inactive'}`}>
	<div className="tamcare-medication-chip__main">
		<b>{med.medicationName}</b> - {med.dosage || 'N/A'}
	</div>
	<div className="tamcare-medication-chip__meta">
		<span><strong>Bắt đầu:</strong> {formatDate(med.startDate)}</span>
		<span><strong>Kết thúc:</strong> {formatDate(med.endDate)}</span>
		<span className={`tamcare-status-pill ${isMedicationActive(med.isActive) ? 'tamcare-status-pill--active' : 'tamcare-status-pill--inactive'}`}>
			{isMedicationActive(med.isActive) ? 'Đang dùng' : 'Ngừng'}
		</span>
	</div>
</div>
{isRelative && (
	<>
		{' '}
		<button type="button" className="tamcare-btn tamcare-btn-view tamcare-btn--small" onClick={() => openMedicationEditor(record, med)}>
			Sửa
		</button>
		{' '}
		<button type="button" className="tamcare-btn tamcare-btn--danger tamcare-btn--small" onClick={() => handleDeleteMedication(record, med)}>
			Xóa
		</button>
	</>
)}
</li>
))}
</ul>
{isRelative && (
	<div style={{ marginTop: 12 }}>
		<button type="button" className="tamcare-btn tamcare-btn-save" onClick={() => openAddMedicationModal(record)}>
			<i className="fa-solid fa-plus"></i> Thêm đơn thuốc cho đợt khám này
		</button>
	</div>
)}
</div>
)}
</div>
</article>
))}
</div>
) : (
<div className="tamcare-empty-state">Chưa có dữ liệu</div>
)}

{assignMessage && <p className="tamcare-form-note">{assignMessage}</p>}
</section>

{/* Form thêm khám/đơn thuốc sẽ chuyển sang Modal để tối ưu UX (người già: ít thao tác; người nhà: giữ ngữ cảnh) */}

<section className="tamcare-medical-section">
<h3><i className="fa-solid fa-pills"></i> Đơn thuốc tổng hợp</h3>
{medicalRecords.some((record) => Array.isArray(record.medications) && record.medications.length > 0) ? (
<div className="tamcare-medication-summary">
{medicalRecords.map((record) => (
<div className="tamcare-medication-group" key={record.id}>
<strong>{record.hospitalName || 'Đợt khám'}</strong>
<p>{Array.isArray(record.medications) ? `${record.medications.length} thuốc` : '0 thuốc'}</p>
</div>
))}
</div>
) : (
<div className="tamcare-empty-state">Chưa có đơn thuốc nào được gắn với lịch sử khám.</div>
)}
</section>
</>
)}

</div>

{addRecordModalOpen && (
	<div className="tamcare-modal" onClick={(e) => { if (e.target.className === 'tamcare-modal') setAddRecordModalOpen(false); }}>
		<div className="tamcare-modal-content tamcare-modal-content--add-record">
			<button type="button" className="tamcare-close-modal tamcare-no-print" onClick={() => {
				setAddRecordModalOpen(false);
				setSelectedElderlyForNewRecord('');
				setRecordForm({
					hospitalName: '',
					diagnosis: '',
					doctorNotes: '',
					visitDate: new Date().toISOString().split('T')[0],
					documentFile: null,
				});
				setAddMedicationSectionOpen(false);
				setRecordMessage('');
			}}>&times;</button>

			<h2 className="tamcare-modal-title">➕ Thêm lịch sử khám mới</h2>
			<p className="tamcare-modal-subtitle">Nhập thông tin khám bệnh và (tuỳ chọn) kèm danh sách thuốc.</p>

				<form className="tamcare-add-form" onSubmit={handleAddRecord}>
				<div className="tamcare-form-grid">
			{isRelative && !profileId && (
						<label className="tamcare-form-grid--full">
							<strong>👤 Chọn người cao tuổi cần ghi nhận</strong>
							{manageableElderlies.length === 0 ? (
								<p className="tamcare-form-note">Chưa có người cao tuổi nào đã mua gói hội viên.</p>
							) : (
							<select 
								value={selectedElderlyForNewRecord} 
								onChange={(e) => setSelectedElderlyForNewRecord(e.target.value)}
								required
								style={{ 
									padding: '12px 16px',
									fontSize: '16px',
									fontWeight: '700',
									borderRadius: '8px',
									border: '2px solid #2c5282'
								}}
							>
								<option value="">-- Chọn người cao tuổi --</option>
								{manageableElderlies.map((elderly) => (
									<option key={elderly.id} value={String(elderly.id)}>
										{elderly.fullName} (ID: {elderly.id})
									</option>
								))}
							</select>
							)}
						</label>
					)}
					<label>
						Bệnh viện / phòng khám
						<input type="text" value={recordForm.hospitalName} onChange={(e) => setRecordForm((prev) => ({ ...prev, hospitalName: e.target.value }))} required />
					</label>
					<label>
						Ngày khám
						<input type="date" value={recordForm.visitDate} onChange={(e) => setRecordForm((prev) => ({ ...prev, visitDate: e.target.value }))} required />
					</label>
					<label>
						Ngày tái khám (tuỳ chọn)
						<input type="datetime-local" value={recordForm.revisitAt || ''} onChange={(e) => setRecordForm((prev) => ({ ...prev, revisitAt: e.target.value }))} />
					</label>
					<label>
						Nhắc trước tái khám (phút)
						<input type="number" value={recordForm.revisitRemindBeforeMinutes || 60} onChange={(e) => setRecordForm((prev) => ({ ...prev, revisitRemindBeforeMinutes: parseInt(e.target.value) || 60 }))} min="0" />
					</label>
					<label className="tamcare-form-grid--full">
						Chẩn đoán
						<textarea value={recordForm.diagnosis} onChange={(e) => setRecordForm((prev) => ({ ...prev, diagnosis: e.target.value }))} required />
					</label>
					<label className="tamcare-form-grid--full">
						Lời dặn bác sĩ
						<textarea value={recordForm.doctorNotes} onChange={(e) => setRecordForm((prev) => ({ ...prev, doctorNotes: e.target.value }))} />
					</label>
					<label className="tamcare-form-grid--full">
						Upload file đơn thuốc/PDF (tuỳ chọn)
						<input type="file" accept="image/*,.pdf" onChange={(e) => setRecordForm((prev) => ({ ...prev, documentFile: e.target.files?.[0] || null }))} />
					</label>
				</div>

				<div className="tamcare-modal-actions">
					<button
						 type="button"
						 className={`tamcare-btn tamcare-btn-expand ${addMedicationSectionOpen ? 'tamcare-btn-expand--active' : ''}`}
						 onClick={() => setAddMedicationSectionOpen((prev) => !prev)}
					>
						<i className="fa-solid fa-capsules"></i> Nhập danh sách thuốc cho đợt khám này
					</button>
				</div>

				<div className="tamcare-form-actions">
					<button type="submit" className="tamcare-btn tamcare-btn-save" disabled={savingRecord}>
						{i18nOrLoading(savingRecord)}
					</button>
				</div>

				{recordMessage && <p className="tamcare-form-note">{recordMessage}</p>}
			</form>

			{addMedicationSectionOpen && (
				<div className="tamcare-add-medications">
					<h3>Thuốc cho đợt khám này</h3>
					<form className="tamcare-edit-form" onSubmit={handleAddMedication}>
						<div className="tamcare-form-grid">
							<label>
								Chọn đợt khám
									<select
										value={medicationForm.medicalRecordId}
										onChange={(e) => setMedicationForm((prev) => ({ ...prev, medicalRecordId: e.target.value }))}
										required
									>
									{medicalRecords.map((record) => (
										<option key={record.id} value={record.id}>
											{formatDate(record.visitDate)} - {record.hospitalName || `#${record.id}`}
										</option>
									))}
								</select>
								</label>
								<label>
									Tên thuốc
									<input type="text" value={medicationForm.medicationName} onChange={(e) => setMedicationForm((prev) => ({ ...prev, medicationName: e.target.value }))} required />
								</label>
								<label>
									Liều dùng
									<input type="text" value={medicationForm.dosage} onChange={(e) => setMedicationForm((prev) => ({ ...prev, dosage: e.target.value }))} required />
								</label>
								<label>
									Ngày bắt đầu
									<input type="date" value={medicationForm.startDate} onChange={(e) => setMedicationForm((prev) => ({ ...prev, startDate: e.target.value }))} required />
								</label>
								<label>
									Ngày kết thúc
									<input type="date" value={medicationForm.endDate} onChange={(e) => setMedicationForm((prev) => ({ ...prev, endDate: e.target.value }))} required />
								</label>
								<label className="tamcare-form-grid--full">
									Giờ nhắc
									<div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
										{parseSchedulesText(medicationForm.schedulesText).map((t) => (
											<span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: '#eef2ff', color: '#1e293b', fontWeight: 700 }}>
												⏰ {t}
												<button type="button" onClick={() => setMedicationForm((prev) => ({ ...prev, schedulesText: removeTimeFromText(prev.schedulesText, t) }))} style={{ border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer', fontWeight: 800, padding: 0 }}>×</button>
											</span>
										))}
										<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
											<input type="time" value={newScheduleTime} onChange={(e) => setNewScheduleTime(e.target.value)} />
											<button type="button" className="tamcare-btn tamcare-btn-save" onClick={() => setMedicationForm((prev) => ({ ...prev, schedulesText: mergeTimesText(prev.schedulesText, newScheduleTime) }))}>Thêm giờ</button>
										</div>
									</div>
								</label>
								<label className="tamcare-form-grid--full">
									Các ngày uống
									<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
										{[1, 2, 3, 4, 5, 6, 0].map((day) => (
											<button
												key={day}
												type="button"
												onClick={() => toggleDay(day)}
												style={{
													padding: '8px 16px',
													borderRadius: 999,
													border: '1px solid #cbd5e1',
													background: (medicationForm.selectedDays || []).includes(day) ? '#0f766e' : 'transparent',
													color: (medicationForm.selectedDays || []).includes(day) ? '#ffffff' : '#334155',
													fontWeight: (medicationForm.selectedDays || []).includes(day) ? 700 : 400,
													cursor: 'pointer',
													transition: 'all 0.2s'
												}}
											>
												{day === 0 ? 'CN' : `T${day + 1}`}
											</button>
										))}
									</div>
									<div style={{ marginTop: 6, color: '#64748b', fontSize: 14 }}>
										{(medicationForm.selectedDays || []).length === 0 ? 'Mỗi ngày' : `Chọn: ${(medicationForm.selectedDays || []).map(d => d === 0 ? 'CN' : `T${d + 1}`).join(', ')}`}
									</div>
								</label>
								<label className="tamcare-form-grid--full">
									Ghi chú
									<textarea value={medicationForm.note} onChange={(e) => setMedicationForm((prev) => ({ ...prev, note: e.target.value }))} />
								</label>
							</div>
							<div className="tamcare-form-actions">
								<button type="submit" className="tamcare-btn tamcare-btn-save" disabled={savingMedication}>
									{i18nOrLoading(savingMedication, 'Lưu đơn thuốc', 'Đang lưu...')}
								</button>
							</div>
							{medicationMessage && <p className="tamcare-form-note">{medicationMessage}</p>}
					</form>
				</div>
			)}
		</div>
	</div>
)}

{scanModalOpen && (
	<div className="tamcare-modal" onClick={(e) => { if (e.target.className === 'tamcare-modal') setScanModalOpen(false); }}>
		<div className="tamcare-modal-content tamcare-modal-content--add-record">
			<button type="button" className="tamcare-close-modal tamcare-no-print" onClick={() => setScanModalOpen(false)}>&times;</button>
			<h2 className="tamcare-modal-title">📷 Quét ảnh tạo hồ sơ</h2>
			<p className="tamcare-modal-subtitle">Tải ảnh/PDF đơn thuốc, hệ thống sẽ trích xuất thông tin (nếu đã cấu hình OCR).</p>

			<form className="tamcare-add-form" onSubmit={handleScanUpload}>
				<div className="tamcare-form-grid">
					{isRelative && !profileId && (
						<label className="tamcare-form-grid--full">
							<strong>👤 Chọn người cao tuổi cần ghi nhận</strong>
							{manageableElderlies.length === 0 ? (
								<p className="tamcare-form-note">Chưa có người cao tuổi nào đã mua gói hội viên.</p>
							) : (
							<select value={selectedElderlyForNewRecord} onChange={(e) => setSelectedElderlyForNewRecord(e.target.value)} required>
								<option value="">-- Chọn người cao tuổi --</option>
								{manageableElderlies.map((elderly) => (
									<option key={elderly.id} value={String(elderly.id)}>{elderly.fullName} (ID: {elderly.id})</option>
								))}
							</select>
							)}
						</label>
					)}
					<label className="tamcare-form-grid--full">
						Tải ảnh/PDF
						<input type="file" accept="image/*,.pdf" onChange={(e) => setScanFile(e.target.files?.[0] || null)} />
					</label>
					{scanDocumentUrl && (
						<div className="tamcare-form-grid--full" style={{ marginTop: 6 }}>
							<small>File đã lưu: <a href={`${API_BASE}${scanDocumentUrl}`} target="_blank" rel="noreferrer">{scanDocumentUrl}</a></small>
						</div>
					)}
					<label>
						Bệnh viện / phòng khám
						<input type="text" value={scanFields.hospitalName} onChange={(e) => setScanFields(prev => ({ ...prev, hospitalName: e.target.value }))} />
					</label>
					<label>
						Ngày khám
						<input type="date" value={scanFields.visitDate} onChange={(e) => setScanFields(prev => ({ ...prev, visitDate: e.target.value }))} />
					</label>
					<label>
						Ngày tái khám (tuỳ chọn)
						<input type="datetime-local" value={scanFields.revisitAt || ''} onChange={(e) => setScanFields(prev => ({ ...prev, revisitAt: e.target.value }))} />
					</label>
					<label>
						Nhắc trước tái khám (phút)
						<input type="number" value={scanFields.revisitRemindBeforeMinutes || 60} onChange={(e) => setScanFields(prev => ({ ...prev, revisitRemindBeforeMinutes: parseInt(e.target.value) || 60 }))} min="0" />
					</label>
					<label className="tamcare-form-grid--full">
						Chẩn đoán
						<textarea value={scanFields.diagnosis} onChange={(e) => setScanFields(prev => ({ ...prev, diagnosis: e.target.value }))} />
					</label>
					<label className="tamcare-form-grid--full">
						Lời dặn bác sĩ
						<textarea value={scanFields.doctorNotes} onChange={(e) => setScanFields(prev => ({ ...prev, doctorNotes: e.target.value }))} />
					</label>
				</div>

				<div className="tamcare-form-actions" style={{ display: 'flex', gap: 8 }}>
					<button type="submit" className="tamcare-btn tamcare-btn-primary" disabled={scanning}>
						{i18nOrLoading(scanning, 'Quét ảnh', 'Đang quét...')}
					</button>
					<button type="button" className="tamcare-btn tamcare-btn-save" disabled={scanning} onClick={handleCreateRecordFromScan}>
						Lưu thông tin khám bệnh
					</button>
				</div>

				{scanMessage && <p className="tamcare-form-note">{scanMessage}</p>}
			</form>
		</div>
	</div>
)}

{addMedicationModalOpen && (
	<div className="tamcare-modal" onClick={(e) => { if (e.target.className === 'tamcare-modal') closeAddMedicationModal(); }}>
		<div className="tamcare-modal-content tamcare-modal-content--add-medication">
			<button type="button" className="tamcare-close-modal tamcare-no-print" onClick={closeAddMedicationModal}>&times;</button>
			<h2 className="tamcare-modal-title">💊 Thêm đơn thuốc</h2>
			<p className="tamcare-modal-subtitle">Gắn thuốc vào đúng đợt khám của người cao tuổi.</p>

			<form className="tamcare-edit-form" onSubmit={handleAddMedication}>
				<div className="tamcare-form-grid">
					<label>
						Chọn đợt khám
							<select value={medicationForm.medicalRecordId} onChange={(e) => setMedicationForm((prev) => ({ ...prev, medicalRecordId: e.target.value }))} required>
								{medicalRecords.map((record) => (
								<option key={record.id} value={record.id}>
									{formatDate(record.visitDate)} - {record.hospitalName || `#${record.id}`}
								</option>
							))}
						</select>
					</label>
					<label>
						Tên thuốc
						<input type="text" value={medicationForm.medicationName} onChange={(e) => setMedicationForm((prev) => ({ ...prev, medicationName: e.target.value }))} required />
					</label>
					<label>
						Liều dùng
						<input type="text" value={medicationForm.dosage} onChange={(e) => setMedicationForm((prev) => ({ ...prev, dosage: e.target.value }))} required />
					</label>
					<label>
						Ngày bắt đầu
						<input type="date" value={medicationForm.startDate} onChange={(e) => setMedicationForm((prev) => ({ ...prev, startDate: e.target.value }))} required />
					</label>
					<label>
						Ngày kết thúc
						<input type="date" value={medicationForm.endDate} onChange={(e) => setMedicationForm((prev) => ({ ...prev, endDate: e.target.value }))} required />
					</label>
					<label className="tamcare-form-grid--full">
						Giờ nhắc
						<div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
							{parseSchedulesText(medicationForm.schedulesText).map((t) => (
								<span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: '#eef2ff', color: '#1e293b', fontWeight: 700, userSelect: 'none' }}>
									<span>⏰ {t}</span>
									<button
										type="button"
										onClick={(e) => { e.stopPropagation(); setMedicationForm((prev) => ({ ...prev, schedulesText: removeTimeFromText(prev.schedulesText, t) })); }}
										style={{ border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer', fontWeight: 800, padding: '0 4px', lineHeight: 1 }}
										aria-label={`Xóa giờ ${t}`}
									>
										×
									</button>
								</span>
							))}
							<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
								<input type="time" value={newScheduleTime} onChange={(e) => setNewScheduleTime(e.target.value)} />
								<button type="button" className="tamcare-btn tamcare-btn-save" onClick={() => setMedicationForm((prev) => ({ ...prev, schedulesText: mergeTimesText(prev.schedulesText, newScheduleTime) }))}>Thêm giờ</button>
							</div>
						</div>
					</label>
					<label className="tamcare-form-grid--full">
						Các ngày uống
						<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
							{[1, 2, 3, 4, 5, 6, 0].map((day) => (
								<button
									key={day}
									type="button"
									onClick={() => toggleDay(day)}
									style={{
										padding: '8px 16px',
										borderRadius: 999,
										border: '1px solid #cbd5e1',
										background: (medicationForm.selectedDays || []).includes(day) ? '#0f766e' : 'transparent',
										color: (medicationForm.selectedDays || []).includes(day) ? '#ffffff' : '#334155',
										fontWeight: (medicationForm.selectedDays || []).includes(day) ? 700 : 400,
										cursor: 'pointer',
										transition: 'all 0.2s'
									}}
								>
									{day === 0 ? 'CN' : `T${day + 1}`}
								</button>
							))}
						</div>
						<div style={{ marginTop: 6, color: '#64748b', fontSize: 14 }}>
							{(medicationForm.selectedDays || []).length === 0 ? 'Mỗi ngày' : `Chọn: ${(medicationForm.selectedDays || []).map(d => d === 0 ? 'CN' : `T${d + 1}`).join(', ')}`}
						</div>
					</label>
					<label className="tamcare-form-grid--full">
						Ghi chú
						<textarea value={medicationForm.note} onChange={(e) => setMedicationForm((prev) => ({ ...prev, note: e.target.value }))} />
					</label>
				</div>
				<div className="tamcare-form-actions">
					<button type="submit" className="tamcare-btn tamcare-btn-save" disabled={savingMedication}>
						{i18nOrLoading(savingMedication, 'Lưu đơn thuốc', 'Đang lưu...')}
					</button>
				</div>
				{medicationMessage && <p className="tamcare-form-note">{medicationMessage}</p>}
			</form>
		</div>
	</div>
)}

{editMedicationModalOpen && editingMedication && (
	<div className="tamcare-modal" onClick={(e) => { if (e.target.className === 'tamcare-modal') setEditMedicationModalOpen(false); }}>
		<div className="tamcare-modal-content tamcare-modal-content--edit-medication">
			<button type="button" className="tamcare-close-modal tamcare-no-print" onClick={() => setEditMedicationModalOpen(false)}>&times;</button>

			<h2 className="tamcare-modal-title">✏️ Sửa đơn thuốc</h2>
			<p className="tamcare-modal-subtitle">Cập nhật thuốc và lịch nhắc cho đợt khám này.</p>

			<form className="tamcare-edit-form" onSubmit={handleEditMedication}>
				<div className="tamcare-form-grid">
					<label>
						Chọn đợt khám
							<select
								value={editingMedication.medicalRecordId}
								onChange={(e) => setEditingMedication((prev) => ({ ...prev, medicalRecordId: e.target.value }))}
								required
							>
								{medicalRecords.map((record) => (
								<option key={record.id} value={record.id}>
									{formatDate(record.visitDate)} - {record.hospitalName || `#${record.id}`}
								</option>
							))}
						</select>
					</label>
					<label>
						Tên thuốc
						<input type="text" value={editingMedication.medicationName} onChange={(e) => setEditingMedication((prev) => ({ ...prev, medicationName: e.target.value }))} required />
					</label>
					<label>
						Liều dùng
						<input type="text" value={editingMedication.dosage} onChange={(e) => setEditingMedication((prev) => ({ ...prev, dosage: e.target.value }))} required />
					</label>
					<label>
						Ngày bắt đầu
						<input type="date" value={editingMedication.startDate} onChange={(e) => setEditingMedication((prev) => ({ ...prev, startDate: e.target.value }))} required />
					</label>
					<label>
						Ngày kết thúc
						<input type="date" value={editingMedication.endDate} onChange={(e) => setEditingMedication((prev) => ({ ...prev, endDate: e.target.value }))} required />
					</label>
					<label>
						Trạng thái
						<select value={editingMedication.isActive} onChange={(e) => setEditingMedication((prev) => ({ ...prev, isActive: e.target.value }))}>
							<option value="1">Đang dùng</option>
							<option value="0">Ngưng</option>
						</select>
					</label>
					<label className="tamcare-form-grid--full">
						Giờ nhắc
						<div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 6 }}>
							{parseSchedulesText(editingMedication.schedulesText).map((t) => (
								<span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 999, background: '#eef2ff', color: '#1e293b', fontWeight: 700, userSelect: 'none' }}>
									<span>⏰ {t}</span>
									<button
										type="button"
										onClick={(e) => { e.stopPropagation(); setEditingMedication((prev) => ({ ...prev, schedulesText: removeTimeFromText(prev.schedulesText, t) })); }}
										style={{ border: 'none', background: 'transparent', color: '#475569', cursor: 'pointer', fontWeight: 800, padding: '0 4px', lineHeight: 1 }}
										aria-label={`Xóa giờ ${t}`}
									>
										×
									</button>
								</span>
							))}
							<div style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
								<input type="time" value={newEditScheduleTime} onChange={(e) => setNewEditScheduleTime(e.target.value)} />
								<button type="button" className="tamcare-btn tamcare-btn-save" onClick={() => setEditingMedication((prev) => ({ ...prev, schedulesText: mergeTimesText(prev.schedulesText, newEditScheduleTime) }))}>Thêm giờ</button>
							</div>
						</div>
					</label>
					<MedicationDaysField
						selectedDays={editingMedication.selectedDays}
						onChange={(days) => setEditingMedication((prev) => ({ ...prev, selectedDays: days }))}
					/>
					<label className="tamcare-form-grid--full">
						Ghi chú
						<textarea value={editingMedication.note} onChange={(e) => setEditingMedication((prev) => ({ ...prev, note: e.target.value }))} />
					</label>
				</div>
				<div className="tamcare-form-actions">
					<button type="submit" className="tamcare-btn tamcare-btn-save" disabled={savingEditedMedication}>
						{i18nOrLoading(savingEditedMedication, 'Lưu thay đổi', 'Đang lưu...')}
					</button>
				</div>
				{editMedicationMessage && <p className="tamcare-form-note">{editMedicationMessage}</p>}
			</form>
		</div>
	</div>
)}

{editRecordModalOpen && editingRecord && (
	<div className="tamcare-modal" onClick={(e) => { if (e.target.className === 'tamcare-modal') setEditRecordModalOpen(false); }}>
		<div className="tamcare-modal-content tamcare-modal-content--add-record">
			<button type="button" className="tamcare-close-modal tamcare-no-print" onClick={() => setEditRecordModalOpen(false)}>&times;</button>
			<h2 className="tamcare-modal-title">Sửa lịch sử khám bệnh</h2>
			<form className="tamcare-add-form" onSubmit={handleEditRecord}>
				<div className="tamcare-form-grid">
					<label>
						Bệnh viện / phòng khám
						<input type="text" value={editingRecord.hospitalName} onChange={(e) => setEditingRecord((prev) => ({ ...prev, hospitalName: e.target.value }))} required />
					</label>
					<label>
						Ngày khám
						<input type="date" value={editingRecord.visitDate} onChange={(e) => setEditingRecord((prev) => ({ ...prev, visitDate: e.target.value }))} required />
					</label>
					<label>
						Ngày tái khám (tuỳ chọn)
						<input type="datetime-local" value={editingRecord.revisitAt || ''} onChange={(e) => setEditingRecord((prev) => ({ ...prev, revisitAt: e.target.value }))} />
					</label>
					<label>
						Nhắc trước tái khám (phút)
						<input type="number" value={editingRecord.revisitRemindBeforeMinutes || 60} onChange={(e) => setEditingRecord((prev) => ({ ...prev, revisitRemindBeforeMinutes: parseInt(e.target.value) || 60 }))} min="0" />
					</label>
					<label className="tamcare-form-grid--full">
						Chẩn đoán
						<textarea value={editingRecord.diagnosis} onChange={(e) => setEditingRecord((prev) => ({ ...prev, diagnosis: e.target.value }))} required />
					</label>
					<label className="tamcare-form-grid--full">
						Lời dặn bác sĩ
						<textarea value={editingRecord.doctorNotes} onChange={(e) => setEditingRecord((prev) => ({ ...prev, doctorNotes: e.target.value }))} />
					</label>
					<label className="tamcare-form-grid--full">
						Link file đính kèm (nếu có)
						<input type="text" value={editingRecord.documentUrl} onChange={(e) => setEditingRecord((prev) => ({ ...prev, documentUrl: e.target.value }))} />
					</label>
				</div>
				<div className="tamcare-form-actions">
					<button type="submit" className="tamcare-btn tamcare-btn-save" disabled={savingEditedRecord}>
						{savingEditedRecord ? 'Đang lưu...' : 'Lưu cập nhật'}
					</button>
				</div>
				{editRecordMessage && <p className="tamcare-form-note">{editRecordMessage}</p>}
			</form>
		</div>
	</div>
)}

{viewerOpen && viewerRecord && (
<div className="tamcare-modal" onClick={(e) => { if (e.target.className === 'tamcare-modal') setViewerOpen(false); }}>
<div className="tamcare-modal-content">
<button type="button" className="tamcare-close-modal tamcare-no-print" onClick={() => setViewerOpen(false)}>&times;</button>

<div className="tamcare-pdf-header">
<h1>{viewerRecord.hospitalName || 'ĐƠN THUỐC GỐC'}</h1>
<p>Ngày khám: {formatDate(viewerRecord.visitDate)}</p>
<p><i>Đơn thuốc / tài liệu gốc đính kèm hồ sơ</i></p>
</div>

<section className="tamcare-pdf-section">
<h4><i className="fa-solid fa-file-medical"></i> Tài liệu gốc</h4>
{previewSrc ? (
<div className="tamcare-document-preview">
<img src={previewSrc} alt="Tài liệu y tế gốc" />
</div>
) : (
<div className="tamcare-empty-state">Không có ảnh xem trước.</div>
)}
</section>

<section className="tamcare-pdf-section">
<h4><i className="fa-solid fa-pills"></i> Đơn thuốc của đợt khám</h4>
{viewerLoading ? (
<div className="tamcare-empty-state">Đang tải danh sách thuốc...</div>
) : viewerError ? (
<div className="tamcare-empty-state tamcare-empty-state--error">{viewerError}</div>
) : viewerMedications.length > 0 ? (
<table className="tamcare-pdf-table">
<thead>
<tr><th>Tên thuốc</th><th>Liều dùng</th><th>Bắt đầu</th><th>Kết thúc</th><th>Trạng thái</th><th>Ghi chú</th><th>Hành động</th></tr>
</thead>
<tbody>
{viewerMedications.map((medication) => (
<tr key={medication.id}>
<td><b>{medication.medicationName}</b></td>
<td>{medication.dosage || 'N/A'}</td>
<td>{formatDate(medication.startDate)}</td>
<td>{formatDate(medication.endDate)}</td>
<td>{Number(medication.isActive) === 1 ? 'Đang dùng' : 'Ngưng'}</td>
<td>{medication.note || 'N/A'}</td>
<td>
	{isRelative && (
		<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
			<button type="button" className="tamcare-btn tamcare-btn-view tamcare-btn--small" onClick={() => openMedicationEditor(viewerRecord, medication)}>
				Sửa
			</button>
			<button type="button" className="tamcare-btn tamcare-btn--danger tamcare-btn--small" onClick={() => handleDeleteMedication(viewerRecord, medication)}>
				Xóa
			</button>
		</div>
	)}
</td>
</tr>
))}
</tbody>
</table>
) : (
<div className="tamcare-empty-state">Không có đơn thuốc kèm theo cho đợt khám này</div>
)}

{isRelative && editingMedication && (
	<div className="tamcare-inline-edit-wrap">
		<h4>✏️ Sửa đơn thuốc</h4>
		<form className="tamcare-edit-form" onSubmit={handleEditMedication}>
			<div className="tamcare-form-grid">
				<label>
					Chọn đợt khám
					<select value={editingMedication.medicalRecordId} onChange={(e) => setEditingMedication((prev) => ({ ...prev, medicalRecordId: e.target.value }))} required>
						{medicalRecords.map((record) => (
							<option key={record.id} value={record.id}>
								{formatDate(record.visitDate)} - {record.hospitalName || `#${record.id}`}
							</option>
						))}
					</select>
				</label>
				<label>
					Tên thuốc
					<input type="text" value={editingMedication.medicationName} onChange={(e) => setEditingMedication((prev) => ({ ...prev, medicationName: e.target.value }))} required />
				</label>
				<label>
					Liều dùng
					<input type="text" value={editingMedication.dosage} onChange={(e) => setEditingMedication((prev) => ({ ...prev, dosage: e.target.value }))} required />
				</label>
				<label>
					Ngày bắt đầu
					<input type="date" value={editingMedication.startDate} onChange={(e) => setEditingMedication((prev) => ({ ...prev, startDate: e.target.value }))} required />
				</label>
				<label>
					Ngày kết thúc
					<input type="date" value={editingMedication.endDate} onChange={(e) => setEditingMedication((prev) => ({ ...prev, endDate: e.target.value }))} required />
				</label>
				<label>
					Trạng thái
					<select value={editingMedication.isActive} onChange={(e) => setEditingMedication((prev) => ({ ...prev, isActive: e.target.value }))}>
						<option value="1">Đang dùng</option>
						<option value="0">Ngưng</option>
					</select>
				</label>
				<label className="tamcare-form-grid--full">
					Giờ nhắc (cách nhau bằng dấu phẩy)
					<input type="text" value={editingMedication.schedulesText} onChange={(e) => setEditingMedication((prev) => ({ ...prev, schedulesText: e.target.value }))} placeholder="08:00,20:00" />
				</label>
				<MedicationDaysField
					selectedDays={editingMedication.selectedDays}
					onChange={(days) => setEditingMedication((prev) => ({ ...prev, selectedDays: days }))}
				/>
				<label className="tamcare-form-grid--full">
					Ghi chú
					<textarea value={editingMedication.note} onChange={(e) => setEditingMedication((prev) => ({ ...prev, note: e.target.value }))} />
				</label>
			</div>
			<div className="tamcare-form-actions">
				<button type="submit" className="tamcare-btn tamcare-btn-save" disabled={savingEditedMedication}>
					{i18nOrLoading(savingEditedMedication, 'Lưu thay đổi', 'Đang lưu...')}
				</button>
				<button type="button" className="tamcare-btn tamcare-btn-view" onClick={closeInlineMedicationEditor}>
					Hủy
				</button>
			</div>
			{editMedicationMessage && <p className="tamcare-form-note">{editMedicationMessage}</p>}
		</form>
	</div>
)}

{isRelative && (
	<div className="tamcare-print-wrap tamcare-no-print" style={{ marginTop: 20 }}>
		<button className="tamcare-btn tamcare-btn-save" type="button" onClick={() => openAddMedicationModal(viewerRecord)}>
			<i className="fa-solid fa-plus"></i> Thêm đơn thuốc cho đợt khám này
		</button>
	</div>
)}
</section>

<div className="tamcare-sign-block">
<p><i>Ký tên xác nhận hệ thống TamCare</i></p>
<p><strong>Hệ thống quản lý y tế điện tử</strong></p>
</div>

<div className="tamcare-print-wrap tamcare-no-print">
<button className="tamcare-btn tamcare-btn-save" type="button" onClick={() => window.print()}>
<i className="fa-solid fa-print"></i> In hồ sơ PDF
</button>
</div>
</div>
</div>
)}

{assignConfirmOpen && (
	<div className="tamcare-confirm-overlay" onClick={(e) => { if (e.target.className === 'tamcare-confirm-overlay') setAssignConfirmOpen(false); }}>
		<div className="tamcare-confirm-card">
			<button type="button" className="tamcare-confirm-close" onClick={() => setAssignConfirmOpen(false)}>
				&times;
			</button>
			<div className="tamcare-confirm-icon">!</div>
			<h2 className="tamcare-confirm-title">Xác nhận đổi người cao tuổi</h2>
			<p className="tamcare-confirm-text">
				Bạn có chắc muốn đổi người cao tuổi cho lịch sử khám này không?
				Hành động này có thể làm thay đổi dữ liệu liên quan.
			</p>
			<div className="tamcare-confirm-actions">
				<button type="button" className="tamcare-confirm-cancel" onClick={() => setAssignConfirmOpen(false)}>
					Hủy
				</button>
				<button type="button" className="tamcare-confirm-confirm" onClick={confirmAssignElderly} disabled={assigningRecordId !== null}>
					Xác nhận
				</button>
			</div>
		</div>
	</div>
)}
</div>
);
};

export default MedicalHistory;
